import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  makeStyles,
  useMediaQuery,
  Tooltip,
  IconButton,
  withStyles,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { primaryColor } from "resources/constants";
import ScaleLoader from "react-spinners/ScaleLoader";
import mixpanelTrack from "app/common/MixpanelComponent";
import { FiMoreVertical } from "react-icons/fi";
import moment from "moment";
import { useEffectAsync } from "lib/react/customHooks";
import getElasticities from "lib/requests/elasticities";
import ElasticityTimeSeriesComponent from "./time_series/ElasticityTimeSeriesComponent";
import ElasticityBubbleMapComponent from "./bubble_map/ElasticityBubbleMapComponent";
import ElasticitySankeyComponent from "./sankey/ElasticitySankeyComponent";
import ExplicabilityComponent from "../explicability-info/ExplicabilityComponent";
import ExportElasticitiesCard from "./export_excel/ExportElasticitiesCard";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("lg")]: {
      overflow: "hidden",
      flexGrow: "1",
    },
  },
  headerContainer: {
    display: "flex",
    borderBottom: "1px solid #e2e2e2",
    marginBottom: "10px",
    marginLeft: "10px",
    marginRight: "10px",
    flexWrap: "nowrap",
    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap",
      paddingBottom: "10px",
    },
  },
  headerTitle: {
    paddingBottom: theme.spacing(1.5),
  },
  iconExportContainer: {
    display: "flex",
    paddingBottom: "10px",
  },
  spinnerContainer: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    justifyContent: "center",
  },
  halfHeight: theme.halfHeight,
  explicabilityContainer: {},
}));

const StyledIconButton = withStyles(() => ({
  root: {
    padding: "0px",
    flex: 1,
    direction: "rtl",
    flexDirection: "row",
  },
}))(IconButton);

const StyledTypography = withStyles(() => ({
  root: {
    flex: 1,
    direction: "ltr",
    flexDirection: "row-reverse",
  },
}))(Typography);

function ElasticityComponent() {
  const classes = useStyles();
  const dataloadSlice = useSelector((state) => state.dataload);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const filtersSlice = useSelector((state) => state.filters);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [minDateExportElasticities, setMinDateExportElasticities] = useState(
    ""
  );
  const [maxDateExportElasticities, setMaxDateExportElasticities] = useState(
    ""
  );
  const [excelGroupBy, setExcelGroupBy] = useState({
    key: "product",
    label: "By Product",
    field: "Sku",
    tooltip: "Sku",
  });
  const [excelGroupByTwo, setExcelGroupByTwo] = useState({
    key: null,
    label: "-",
  });
  const [checkboxChecked, setCheckboxChecked] = useState(true);

  const handleExportExcelClick = () => {
    /* MIXPANEL TRACKS */
    mixpanelTrack(`Open dialog export elasticities`);
    /* END OF MIXPANEL TRACKS */
    setExcelGroupBy({
      key: "product",
      label: "By Product",
      field: "Sku",
      tooltip: "Sku",
    });
    setExcelGroupByTwo({
      key: null,
      label: "-",
    });
    setCheckboxChecked(true);
    setConfirmDialogOpen(true);
  };

  const handleDialogClose = () => {
    setExcelGroupBy({
      key: "product",
      label: "By Product",
      field: "Sku",
      tooltip: "Sku",
    });
    setExcelGroupByTwo({
      key: null,
      label: "-",
    });
    setCheckboxChecked(true);
    setConfirmDialogOpen(false);
  };

  const getElasticitiesApplicableFilters = () => {
    const { filtersKey } = filtersSlice;
    const queryFilters = {
      ...filtersSlice.queryFilters,
    };
    if (queryFilters.category_id) delete queryFilters.category_id;
    if (queryFilters.retailer_group_id) delete queryFilters.retailer_group_id;
    return { filtersKey, queryFilters };
  };

  React.useEffect(() => {
    /* MIXPANEL TRACKS */
    mixpanelTrack("Elasticity module visited");
    /* END OF MIXPANEL TRACKS */
  }, []);

  // eslint-disable-next-line
  const dateRange = {
    min_date: moment()
      .startOf("month")
      .format("YYYY-MM-DD"),
    max_date: moment()
      .add(1, "years")
      .endOf("month")
      .format("YYYY-MM-DD"),
  };

  useEffectAsync(
    async () => {
      const getMinDate = getElasticities({
        group_by: "date",
        order_by: "date",
        limit: 1,
        order_direction: "asc",
        min_date: dateRange.min_date,
      });

      const getMaxDate = getElasticities({
        group_by: "date",
        order_by: "date",
        limit: 1,
        order_direction: "desc",
        max_date: dateRange.max_date,
      });

      const responses = await Promise.all([getMinDate, getMaxDate]);
      return responses;
    },
    (responses) => {
      setMinDateExportElasticities(responses[0].data[0].date);
      setMaxDateExportElasticities(responses[1].data[0].date);
    },
    []
  );

  const { filtersKey, queryFilters } = getElasticitiesApplicableFilters();

  return (
    <>
      <ExportElasticitiesCard
        open={confirmDialogOpen}
        handleClose={handleDialogClose}
        minDate={minDateExportElasticities}
        maxDate={maxDateExportElasticities}
        excelGroupBy={excelGroupBy}
        setExcelGroupBy={setExcelGroupBy}
        excelGroupByTwo={excelGroupByTwo}
        setExcelGroupByTwo={setExcelGroupByTwo}
        checkboxChecked={checkboxChecked}
        setCheckboxChecked={setCheckboxChecked}
      />
      {dataloadSlice.selectedDataload.id ? (
        <>
          <Box className={classes.headerContainer}>
            <StyledTypography variant="h6" className={classes.headerTitle}>
              Price Elasticities
              <ExplicabilityComponent tooltipText="Price elasticity of demand is the main indicator for measuring products market behaviour. Elasticities  quantify  how sensitive my products are to their own price changes (own elasticity) and how sensitive my products are to price changes of others products (cross elasticity)" />
            </StyledTypography>

            <Tooltip title="Export Elasticities" placement="left">
              <Box className={classes.iconExportContainer}>
                <StyledIconButton
                  aria-label="export elasticities"
                  component="span"
                  disableRipple
                  onClick={handleExportExcelClick}
                >
                  <FiMoreVertical size={20} />
                </StyledIconButton>
              </Box>
            </Tooltip>
          </Box>
          <Grid container className={classes.root}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={6}
              className={!isSmallScreen && classes.halfHeight}
            >
              <ElasticityTimeSeriesComponent
                filtersKey={filtersKey}
                queryFilters={queryFilters}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={6}
              className={!isSmallScreen && classes.halfHeight}
            >
              <ElasticityBubbleMapComponent
                filtersKey={filtersKey}
                queryFilters={queryFilters}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              className={!isSmallScreen && classes.halfHeight}
            >
              <ElasticitySankeyComponent
                filtersKey={filtersKey}
                queryFilters={queryFilters}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <Box component="div" className={classes.spinnerContainer}>
          <ScaleLoader size={150} color={primaryColor} loading />
        </Box>
      )}
    </>
  );
}

export default ElasticityComponent;
